import React, { useState, useEffect, useRef } from "react";
import { tokyoNight } from "@uiw/codemirror-theme-tokyo-night";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { Alert, Button, ButtonGroup, Stack } from "@mui/material";
import PromptInput from "./PromptInput";
import { useDispatch, useSelector } from "react-redux";
import { codeEditorActions } from "store/codeEditor";
import { httpsCallable } from "firebase/functions";
import { functions } from "firebaseApp";
import { popupActions } from "store/react-lib/generic-ui/popup";
import { editorActions } from "store/editor";
import { diffWords } from "diff";
import { EditorView, Decoration } from "@codemirror/view";
import { RangeSet } from "@codemirror/state";
import useElementSize from "hooks/react-lib/generic-ui/useElementSize";

const diffStyles = EditorView.baseTheme({
  ".cm-diff-added": { backgroundColor: "#00800020" }, // Light green
  ".cm-diff-removed": { backgroundColor: "#ff000020" }, // Light red
});

// Function to compute diff decorations
function computeDiffDecorations(oldText, newText) {
  if (newText === null) return RangeSet.of([]);
  const diffResult = diffWords(oldText, newText);
  const decorations = [];
  let currentPos = 0;

  diffResult.forEach((part) => {
    const length = part.value.length;
    if (part.added) {
      const decoration = Decoration.mark({
        class: "cm-diff-added",
      }).range(currentPos, currentPos + length);
      decorations.push(decoration);
    } else if (part.removed) {
      const decoration = Decoration.mark({
        class: "cm-diff-removed",
      }).range(currentPos, currentPos + length);
      decorations.push(decoration);
    }
    if (!part.added) {
      currentPos += length;
    }
  });

  return RangeSet.of(decorations);
}

export default function CodeEditor({ documentId, value, setValue, save }) {
  const newCode = useSelector((state) => state.codeEditor?.newCode);
  const [diffDecorations, setDiffDecorations] = useState(RangeSet.of([]));

  useEffect(() => {
    setDiffDecorations(computeDiffDecorations(value, newCode));
  }, [newCode, value]);

  const diffExtension = [
    diffStyles,
    EditorView.decorations.compute(
      [diffDecorations],
      (view) => diffDecorations
    ),
  ];

  const botState = useSelector((state) => state.bot);
  const dispatch = useDispatch();

  const handleSend = async (prompt) => {
    try {
      dispatch(codeEditorActions.setSending(true));
      const payload = {
        botId: botState.current,
        documentId,
        code: value,
        prompt,
      };
      const res = await httpsCallable(functions, "editCode")(payload);
      const resData = res.data;
      if (!resData.success) {
        dispatch(popupActions.showError("An error occurred. Try again later."));
      } else {
        dispatch(codeEditorActions.setNewCode(resData.code));
      }
    } catch (error) {
      dispatch(popupActions.showError("An error occurred. Try again later."));
    } finally {
      dispatch(codeEditorActions.setSending(false));
    }
  };

  const handleAccept = () => {
    dispatch(editorActions.updateActiveDocument(newCode));
    dispatch(codeEditorActions.setNewCode(null));
    dispatch(codeEditorActions.setPrompt(""));
    save(newCode);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "s") {
        event.preventDefault();
        save(value);
      }
      // if ((event.metaKey || event.ctrlKey) && event.key === "z") {
      //   event.preventDefault();
      //   handleUndo();
      // }
      // if ((event.metaKey || event.ctrlKey) && event.key === "y") {
      //   event.preventDefault();
      //   handleRedo();
      // }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const containerRef = useRef();
  const size = useElementSize(containerRef);

  console.log(size);

  return (
    <Stack
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      spacing={2}
    >
      <Alert severity="warning">
        This feature is a work in progress.
      </Alert>

      <Stack
        justifyContent="space-between"
        ref={containerRef}
        direction={"row"}
        sx={{
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        <CodeMirror
          value={value}
          theme={tokyoNight}
          extensions={[python(), javascript()]}
          basicSetup={{
            lineNumbers: true,
          }}
          onChange={setValue}
          height={`${size.height}px`}
          width={`${newCode ? size.width / 2 - 10 : size.width}px`}
        />

        {newCode && (
          <CodeMirror
            value={newCode}
            theme={tokyoNight}
            extensions={[python(), javascript(), diffExtension]}
            editable={false}
            basicSetup={{
              lineNumbers: true,
            }}
            height={`${size.height}px`}
            width={`${size.width / 2 - 10}px`}
          />
        )}
      </Stack>

      {newCode ? (
        <ButtonGroup fullWidth>
          <Button
            color="secondary"
            onClick={() => dispatch(codeEditorActions.setNewCode(null))}
          >
            Reject
          </Button>
          <Button onClick={handleAccept}>Accept</Button>
        </ButtonGroup>
      ) : (
        <PromptInput handleSend={handleSend} />
      )}
    </Stack>
  );
}
